import { useState, useEffect } from 'react';
import type { WorkingSchedule } from '../types/database';

export type WorkingHoursStatus = 'in_hours' | 'out_of_hours' | 'unknown';

/** Convert "HH:mm" to total minutes since midnight. */
function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

const WEEKDAY_MAP: Record<string, string> = {
  Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday',
  Thu: 'thursday', Fri: 'friday', Sat: 'saturday', Sun: 'sunday',
};

/**
 * Pure check — is `date` within the user's configured working hours?
 * Returns `'unknown'` if no schedule is provided or it has no days configured.
 */
export function checkWorkingHours(
  schedule: WorkingSchedule | null | undefined,
  date: Date = new Date(),
): WorkingHoursStatus {
  if (!schedule?.days) return 'unknown';

  const timezone = schedule.timezone || 'Europe/London';

  try {
    const parts = Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';

    const dayKey = WEEKDAY_MAP[get('weekday')];
    if (!dayKey) return 'unknown';

    const day = (schedule.days as any)[dayKey];
    if (!day?.enabled) return 'out_of_hours';

    const current = toMinutes(`${get('hour')}:${get('minute')}`);
    const start   = toMinutes(day.start || '08:00');
    const end     = toMinutes(day.end   || '18:00');

    if (start === end) return 'out_of_hours';

    // Normal case: start < end (e.g. 08:00–18:00)
    if (start < end) {
      return current >= start && current < end ? 'in_hours' : 'out_of_hours';
    }

    // Overnight shift: start > end (e.g. 22:00–06:00)
    return current >= start || current < end ? 'in_hours' : 'out_of_hours';
  } catch {
    return 'unknown';
  }
}

/**
 * React hook — re-evaluates on every minute boundary so the dot updates
 * automatically when the user moves in or out of their working hours.
 */
export function useWorkingHoursStatus(
  schedule: WorkingSchedule | null | undefined,
): WorkingHoursStatus {
  const [status, setStatus] = useState<WorkingHoursStatus>(() =>
    checkWorkingHours(schedule),
  );

  useEffect(() => {
    if (!schedule?.days) {
      setStatus('unknown');
      return;
    }

    const tick = () => setStatus(checkWorkingHours(schedule));
    tick();

    // Align to the next minute boundary so the dot flips exactly at HH:MM:00
    const now = new Date();
    const msToNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    let interval: ReturnType<typeof setInterval>;
    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 60_000);
    }, msToNextMinute);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [schedule]);

  return status;
}
